import React, { useState, useEffect } from 'react';
import { botService } from '../services';
import BackButton from '../components/BackButton';
import '../styles/App.css';

const QQBotConfig = () => {
  const [loading, setLoading] = useState(true);
  const [botStatus, setBotStatus] = useState({ connected: false, hasConfig: false });
  const [config, setConfig] = useState(null);
  const [saveLoading, setSaveLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // View state
  const [view, setView] = useState('list'); // 'list' or 'detail'
  const [selectedRoomIndex, setSelectedRoomIndex] = useState(null);

  useEffect(() => {
    fetchBotData();
    // Poll bot status every 10 seconds
    const interval = setInterval(fetchBotStatus, 10000);
    return () => clearInterval(interval);
  }, []);

  const fetchBotStatus = async () => {
    try {
      const status = await botService.getBotStatus();
      setBotStatus(status);
    } catch (error) {
      console.error('Error fetching bot status:', error);
    }
  };

  const fetchBotData = async () => {
    setLoading(true);
    setError('');
    try {
      const [status, configData] = await Promise.all([
        botService.getBotStatus(),
        botService.getBotConfig()
      ]);
      setBotStatus(status);
      setConfig(configData);
      if (!configData.rooms) {
        setConfig({ rooms: [] });
      }
    } catch (error) {
      console.error('Error fetching bot data:', error);
      setError(error.response?.data?.message || '无法连接到 Bot，请确保 Bot 已启动并连接到服务器');
      try {
        const status = await botService.getBotStatus();
        setBotStatus(status);
      } catch (e) {
        console.error('Error fetching status:', e);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSaveConfig = async (updatedConfig) => {
    setSaveLoading(true);
    setError('');
    setSuccess('');

    try {
      await botService.updateBotConfig(updatedConfig);
      setConfig(updatedConfig);
      setSuccess('配置已成功更新！Bot 会自动重载配置。');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error saving config:', error);
      setError(error.response?.data?.message || '保存配置失败');
    } finally {
      setSaveLoading(false);
    }
  };

  const handleToggleQQNotify = async (index) => {
    const updatedConfig = { ...config };
    updatedConfig.rooms[index].qq_notify_enabled = !updatedConfig.rooms[index].qq_notify_enabled;
    await handleSaveConfig(updatedConfig);
  };

  const handleUpdateRoom = async (index, updates) => {
    const updatedConfig = { ...config };
    updatedConfig.rooms[index] = { ...updatedConfig.rooms[index], ...updates };
    await handleSaveConfig(updatedConfig);
  };

  const handleRefresh = () => {
    fetchBotData();
  };

  if (loading) {
    return (
      <div className="container">
        <BackButton />
        <div className="admin-container">
          <h1>QQ 推送配置</h1>
          <div className="loading">加载中...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <BackButton />
      <div className="admin-container">
        <div className="admin-header">
          <h1>QQ 推送配置</h1>
          <div className="bot-status-indicator">
            <span className={`status-dot ${botStatus.connected ? 'status-connected' : 'status-disconnected'}`}></span>
            <span className="status-text">
              {botStatus.connected ? 'Bot 已连接' : 'Bot 未连接'}
            </span>
          </div>
        </div>

        {error && (
          <div className="alert alert-error">
            {error}
          </div>
        )}

        {success && (
          <div className="alert alert-success">
            {success}
          </div>
        )}

        {!botStatus.connected && (
          <div className="alert alert-warning">
            ⚠️ Bot 未连接到服务器。请确保 Bot 程序正在运行并配置了正确的 WebSocket 地址。
          </div>
        )}

        <div className="bot-config-actions">
          <button 
            className="btn btn-primary" 
            onClick={handleRefresh}
            disabled={!botStatus.connected}
          >
            🔄 刷新配置
          </button>
          
          <a href="/admin/bot" className="btn btn-secondary">
            ← 返回直播 Bot 配置
          </a>
        </div>

        {view === 'list' ? (
          <QQRoomListView 
            config={config}
            botStatus={botStatus}
            onSelectRoom={(index) => {
              setSelectedRoomIndex(index);
              setView('detail');
            }}
            onToggleQQNotify={handleToggleQQNotify}
          />
        ) : (
          <QQRoomDetailView 
            room={config?.rooms[selectedRoomIndex]}
            roomIndex={selectedRoomIndex}
            onBack={() => {
              setView('list');
              setSelectedRoomIndex(null);
            }}
            onUpdate={(updates) => handleUpdateRoom(selectedRoomIndex, updates)}
            saveLoading={saveLoading}
          />
        )}
      </div>
    </div>
  );
};

// QQ Room List View Component
const QQRoomListView = ({ config, botStatus, onSelectRoom, onToggleQQNotify }) => {
  if (!config?.rooms?.length) {
    return (
      <div className="no-config">
        <p>暂无配置的房间</p>
        <p className="help-text">请先在直播 Bot 配置中添加房间</p>
      </div>
    );
  }

  return (
    <div className="room-list">
      {config.rooms.map((room, index) => (
        <div key={index} className={`room-card ${room.qq_notify_enabled ? 'room-enabled' : 'room-disabled'}`}>
          <div className="room-card-header">
            {room.anchor_info?.face ? (
              <img 
                src={room.anchor_info.face} 
                alt={room.anchor_info?.uname || ''}
                className="anchor-avatar"
                referrerPolicy="no-referrer"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            ) : (
              <div className="anchor-avatar-placeholder">
                👤
              </div>
            )}
            
            <div className="room-card-title-area">
              <div className="anchor-name">{room.anchor_info?.uname || `UID: ${room.uid}`}</div>
              <h3>房间 {room.anchor_info?.room_id || room.uid}</h3>
            </div>

            <label className="toggle-switch" onClick={(e) => e.stopPropagation()}>
              <input 
                type="checkbox" 
                checked={room.qq_notify_enabled || false}
                onChange={() => onToggleQQNotify(index)}
                disabled={!botStatus.connected}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>
          
          <div className="room-card-body" onClick={() => onSelectRoom(index)}>
            <div className="qq-targets-summary">
              <div className="qq-targets-count">
                <span className="qq-icon">📱</span>
                <span>已配置 {room.qq_targets?.length || 0} 个推送目标</span>
              </div>
              
              {room.qq_targets && room.qq_targets.length > 0 && (
                <div className="qq-targets-preview">
                  {room.qq_targets.slice(0, 3).map((target, idx) => (
                    <span key={idx} className="qq-target-badge">
                      {target.type === 1 ? '群' : '私聊'}: {target.id}
                    </span>
                  ))}
                  {room.qq_targets.length > 3 && (
                    <span className="qq-target-badge qq-target-more">
                      +{room.qq_targets.length - 3} 更多
                    </span>
                  )}
                </div>
              )}
            </div>
            
            <div className="room-card-footer">
              <span className="click-hint">点击配置推送目标 →</span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// QQ Room Detail View Component
const QQRoomDetailView = ({ room, roomIndex, onBack, onUpdate, saveLoading }) => {
  const [editingTarget, setEditingTarget] = useState(null);
  const [showAddTarget, setShowAddTarget] = useState(false);

  if (!room) return null;

  const handleToggleQQNotify = () => {
    onUpdate({ qq_notify_enabled: !room.qq_notify_enabled });
  };

  const handleAddTarget = (newTarget) => {
    const targets = [...(room.qq_targets || []), newTarget];
    onUpdate({ qq_targets: targets });
    setShowAddTarget(false);
  };

  const handleUpdateTarget = (index, updatedTarget) => {
    const targets = [...(room.qq_targets || [])];
    targets[index] = updatedTarget;
    onUpdate({ qq_targets: targets });
    setEditingTarget(null);
  };

  const handleDeleteTarget = (index) => {
    if (!window.confirm('确定要删除这个推送目标吗？')) return;
    const targets = [...(room.qq_targets || [])];
    targets.splice(index, 1);
    onUpdate({ qq_targets: targets });
  };

  return (
    <div className="room-detail">
      <div className="room-detail-header">
        <button className="btn btn-secondary" onClick={onBack}>
          ← 返回房间列表
        </button>
        <h2>
          {room.anchor_info?.uname || `房间 ${room.anchor_info?.room_id || room.uid}`} - QQ 推送配置
        </h2>
      </div>

      {/* 总开关 */}
      <div className="feature-card">
        <div className="feature-card-header">
          <div className="feature-card-title">
            <span className="feature-icon">📱</span>
            <h3>QQ 推送总开关</h3>
          </div>
          <div className="feature-card-actions">
            <label className="toggle-switch">
              <input 
                type="checkbox" 
                checked={room.qq_notify_enabled || false}
                onChange={handleToggleQQNotify}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>
        </div>
        <div className="feature-card-body">
          <p className="help-text">
            开启后，开播、下播、动态、视频等通知会推送到下方配置的 QQ 群或私聊
          </p>
        </div>
      </div>

      {/* 推送目标列表 */}
      <div className="qq-targets-section">
        <div className="section-header-row">
          <h3>📋 推送目标列表</h3>
          <button 
            className="btn btn-success"
            onClick={() => setShowAddTarget(true)}
          >
            ➕ 添加推送目标
          </button>
        </div>

        {(!room.qq_targets || room.qq_targets.length === 0) ? (
          <div className="no-targets">
            <p>暂无推送目标</p>
            <p className="help-text">点击上方按钮添加 QQ 群或私聊推送</p>
          </div>
        ) : (
          <div className="qq-targets-list">
            {room.qq_targets.map((target, index) => (
              <QQTargetCard 
                key={index}
                target={target}
                index={index}
                onEdit={() => setEditingTarget(index)}
                onDelete={() => handleDeleteTarget(index)}
              />
            ))}
          </div>
        )}
      </div>

      {/* 添加推送目标模态框 */}
      {showAddTarget && (
        <QQTargetEditModal
          target={null}
          onClose={() => setShowAddTarget(false)}
          onSave={handleAddTarget}
          saveLoading={saveLoading}
        />
      )}

      {/* 编辑推送目标模态框 */}
      {editingTarget !== null && (
        <QQTargetEditModal
          target={room.qq_targets[editingTarget]}
          onClose={() => setEditingTarget(null)}
          onSave={(updatedTarget) => handleUpdateTarget(editingTarget, updatedTarget)}
          saveLoading={saveLoading}
        />
      )}
    </div>
  );
};

// QQ Target Card Component
const QQTargetCard = ({ target, index, onEdit, onDelete }) => {
  const getTargetTypeName = (type) => {
    return type === 1 ? 'QQ 群' : '私聊';
  };

  const getEnabledFeatures = (target) => {
    const features = [];
    if (target.live_on?.enabled) features.push('开播通知');
    if (target.live_off?.enabled) features.push('下播通知');
    if (target.dynamic?.enabled) features.push('动态推送');
    if (target.video?.enabled) features.push('视频推送');
    return features;
  };

  const enabledFeatures = getEnabledFeatures(target);

  return (
    <div className="qq-target-card">
      <div className="qq-target-card-header">
        <div className="qq-target-info">
          <span className="qq-target-type-badge">
            {target.type === 1 ? '👥 群' : '💬 私聊'}
          </span>
          <span className="qq-target-id">{target.id}</span>
        </div>
        <div className="qq-target-actions">
          <button className="btn btn-sm btn-primary" onClick={onEdit}>
            ✏️ 编辑
          </button>
          <button className="btn btn-sm btn-danger" onClick={onDelete}>
            🗑️
          </button>
        </div>
      </div>
      
      <div className="qq-target-card-body">
        <div className="qq-target-features">
          {enabledFeatures.length > 0 ? (
            enabledFeatures.map((feature, idx) => (
              <span key={idx} className="feature-badge feature-enabled">
                {feature}
              </span>
            ))
          ) : (
            <span className="feature-badge feature-disabled">未启用任何通知</span>
          )}
        </div>
        
        {target.live_off?.send_report && (
          <div className="qq-target-extra">
            <span className="extra-badge">📊 发送直播报告</span>
          </div>
        )}
      </div>
    </div>
  );
};

// QQ Target Edit Modal Component
const QQTargetEditModal = ({ target, onClose, onSave, saveLoading }) => {
  const [editData, setEditData] = useState(target || {
    type: 1,
    id: 0,
    live_on: {
      enabled: true,
      message: "{uname}开播了{newline}{uname} 正在直播 {title}{newline}{url}{next}{cover}"
    },
    live_off: {
      enabled: true,
      message: "{uname} 下播了~本次直播 {duration}",
      send_report: false
    },
    dynamic: {
      enabled: true,
      message: "{uname} 发布了新动态{card_image}"
    },
    video: {
      enabled: true,
      message: "{uname} 投稿了新视频📺 {title}{next}{url}{next}{card_image}"
    }
  });

  const handleSave = () => {
    if (!editData.id || editData.id <= 0) {
      alert('请输入有效的 QQ 号或群号');
      return;
    }
    onSave(editData);
  };

  const updateField = (field, value) => {
    setEditData({ ...editData, [field]: value });
  };

  const updateNestedField = (category, field, value) => {
    setEditData({
      ...editData,
      [category]: {
        ...editData[category],
        [field]: value
      }
    });
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{target ? '编辑推送目标' : '添加推送目标'}</h3>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        
        <div className="modal-body">
          {/* 基础设置 */}
          <div className="form-section">
            <h4>基础设置</h4>
            
            <FormField label="推送类型">
              <select
                className="form-input"
                value={editData.type}
                onChange={(e) => updateField('type', parseInt(e.target.value))}
              >
                <option value={1}>QQ 群</option>
                <option value={2}>私聊</option>
              </select>
            </FormField>

            <FormField label={editData.type === 1 ? "QQ 群号" : "QQ 号"}>
              <input
                type="number"
                className="form-input"
                value={editData.id || ''}
                onChange={(e) => updateField('id', parseInt(e.target.value) || 0)}
                placeholder={editData.type === 1 ? "请输入 QQ 群号" : "请输入 QQ 号"}
              />
            </FormField>
          </div>

          {/* 开播通知 */}
          <div className="form-section">
            <h4>🔴 开播通知</h4>
            
            <FormField label="启用" inline>
              <ToggleSwitch 
                checked={editData.live_on?.enabled || false}
                onChange={(checked) => updateNestedField('live_on', 'enabled', checked)}
              />
            </FormField>

            {editData.live_on?.enabled && (
              <FormField label="消息模板">
                <textarea
                  className="form-input"
                  value={editData.live_on?.message || ''}
                  onChange={(e) => updateNestedField('live_on', 'message', e.target.value)}
                  rows="4"
                  placeholder="{atall}{newline}🎉 {uname}开播了！{newline}🎬 {title}{newline}🔗 {url}{next}{cover}"
                />
                <div className="template-help">
                  <p className="help-text"><strong>变量说明:</strong></p>
                  <p className="help-text">{'{uname}'} 主播名称 • {'{title}'} 直播标题 • {'{url}'} 直播链接 • {'{room_id}'} 房间号 • {'{cover}'} 封面图片</p>
                  <p className="help-text"><strong>特殊变量:</strong></p>
                  <p className="help-text">{'{next}'} 分条发送 • {'{newline}'} 同条消息内换行 • {'{atall}'} @全体成员</p>
                </div>
              </FormField>
            )}
          </div>

          {/* 下播通知 */}
          <div className="form-section">
            <h4>⚫ 下播通知</h4>
            
            <FormField label="启用" inline>
              <ToggleSwitch 
                checked={editData.live_off?.enabled || false}
                onChange={(checked) => updateNestedField('live_off', 'enabled', checked)}
              />
            </FormField>

            {editData.live_off?.enabled && (
              <>
                <FormField label="消息模板">
                  <textarea
                    className="form-input"
                    value={editData.live_off?.message || ''}
                    onChange={(e) => updateNestedField('live_off', 'message', e.target.value)}
                    rows="3"
                    placeholder="📴 {uname} 下播了~{newline}本次直播时长：{duration}"
                  />
                  <div className="template-help">
                    <p className="help-text"><strong>变量说明:</strong></p>
                    <p className="help-text">{'{uname}'} 主播名称 • {'{duration}'} 直播时长</p>
                    <p className="help-text"><strong>特殊变量:</strong></p>
                    <p className="help-text">{'{next}'} 分条发送 • {'{newline}'} 同条消息内换行</p>
                  </div>
                </FormField>

                <FormField label="发送直播报告" inline>
                  <ToggleSwitch 
                    checked={editData.live_off?.send_report || false}
                    onChange={(checked) => updateNestedField('live_off', 'send_report', checked)}
                  />
                </FormField>
                <p className="help-text">下播时同时发送本场直播的数据统计报告图片</p>
              </>
            )}
          </div>

          {/* 动态推送 */}
          <div className="form-section">
            <h4>📝 动态推送</h4>
            
            <FormField label="启用" inline>
              <ToggleSwitch 
                checked={editData.dynamic?.enabled || false}
                onChange={(checked) => updateNestedField('dynamic', 'enabled', checked)}
              />
            </FormField>

            {editData.dynamic?.enabled && (
              <FormField label="消息模板">
                <textarea
                  className="form-input"
                  value={editData.dynamic?.message || ''}
                  onChange={(e) => updateNestedField('dynamic', 'message', e.target.value)}
                  rows="3"
                  placeholder="📢 {uname} 发布了新动态{next}{card_image}"
                />
                <div className="template-help">
                  <p className="help-text"><strong>变量说明:</strong></p>
                  <p className="help-text">{'{uname}'} 主播名称 • {'{content}'} 动态内容文字 • {'{url}'} 动态链接 • {'{card_image}'} 动态卡片图片</p>
                  <p className="help-text"><strong>特殊变量:</strong></p>
                  <p className="help-text">{'{next}'} 分条发送 • {'{newline}'} 同条消息内换行 • {'{atall}'} @全体成员</p>
                </div>
              </FormField>
            )}
          </div>

          {/* 视频推送 */}
          <div className="form-section">
            <h4>🎬 视频推送</h4>
            
            <FormField label="启用" inline>
              <ToggleSwitch 
                checked={editData.video?.enabled || false}
                onChange={(checked) => updateNestedField('video', 'enabled', checked)}
              />
            </FormField>

            {editData.video?.enabled && (
              <FormField label="消息模板">
                <textarea
                  className="form-input"
                  value={editData.video?.message || ''}
                  onChange={(e) => updateNestedField('video', 'message', e.target.value)}
                  rows="3"
                  placeholder="🎬 {uname} 投稿了新视频{newline}📺 {title}{next}{url}{next}{cover}"
                />
                <div className="template-help">
                  <p className="help-text"><strong>变量说明:</strong></p>
                  <p className="help-text">{'{uname}'} 主播名称 • {'{title}'} 视频标题 • {'{url}'} 视频链接 • {'{cover}'} 视频封面</p>
                  <p className="help-text"><strong>特殊变量:</strong></p>
                  <p className="help-text">{'{next}'} 分条发送 • {'{newline}'} 同条消息内换行 • {'{atall}'} @全体成员</p>
                </div>
              </FormField>
            )}
          </div>

          {/* 变量速查表 */}
          <div className="form-section template-reference">
            <h4>📖 变量速查表</h4>
            <div className="reference-grid">
              <div className="reference-item">
                <code>{'{next}'}</code>
                <span>分条发送 - 将消息分割成多条独立消息</span>
              </div>
              <div className="reference-item">
                <code>{'{newline}'}</code>
                <span>换行 - 在同一条消息内换行</span>
              </div>
              <div className="reference-item">
                <code>{'{atall}'}</code>
                <span>@全体成员 - 需要机器人有管理员权限</span>
              </div>
              <div className="reference-item">
                <code>{'{cover}'}</code>
                <span>封面图片 - 自动转为图片消息</span>
              </div>
              <div className="reference-item">
                <code>{'{card_image}'}</code>
                <span>卡片图片 - 渲染动态/视频卡片</span>
              </div>
              <div className="reference-item">
                <code>{'{pathpic=xxx}'}</code>
                <span>自定义图片 - 发送指定路径的图片</span>
              </div>
            </div>
          </div>
        </div>
        
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            取消
          </button>
          <button 
            className="btn btn-success" 
            onClick={handleSave}
            disabled={saveLoading}
          >
            {saveLoading ? '保存中...' : '保存'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Form Field Component
const FormField = ({ label, children, inline }) => {
  return (
    <div className={`form-field ${inline ? 'form-field-inline' : ''}`}>
      <label className="form-label">{label}</label>
      <div className="form-control">
        {children}
      </div>
    </div>
  );
};

// Toggle Switch Component
const ToggleSwitch = ({ checked, onChange }) => {
  return (
    <label className="toggle-switch">
      <input 
        type="checkbox" 
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span className="toggle-slider"></span>
    </label>
  );
};

export default QQBotConfig;
